import { useMemo, useRef, useState } from 'react';
import { ColDef, ColumnState, RowClassParams, ValueFormatterParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Select from 'src/components/common/gridWrapper/Grid_Select';
import { PDA1040_R01DetailDto } from 'src/containers/PDA/PDA1040_R01/PDA1040_R01Dto';
import { CheckedItem } from './usePDA1040_R01Store';
import {
  aggridHeaderClass,
  pdaCellStyle,
} from 'src/components/common/common';

interface PDA1040_R01TableProps {
  details: PDA1040_R01DetailDto[];
  checkedList: CheckedItem[];
  scannedItemCd?: string | null;
}

type PDA1040_R01DetailDtoWithInserted = PDA1040_R01DetailDto & { inserted?: boolean };

const ACTIVE_ROW_BG = '#fef9c3'; // yellow-100 (스캔·검수 행)

const PDA1040_R01Table: React.FC<PDA1040_R01TableProps> = ({
  details,
  checkedList,
  scannedItemCd,
}) => {
  const gridRef = useRef<AgGridReact<PDA1040_R01DetailDtoWithInserted>>(null);
  const gridWrapperRef = useRef<HTMLDivElement>(null);
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
  const [columnState, setColumnState] = useState<ColumnState[] | null>(null);

  // 검수량 계산 맵 생성
  const checkedQtyMap = useMemo(() => {
    const map = new Map<string, number>();
    checkedList.forEach(item => {
      const current = map.get(item.itemCd) || 0;
      map.set(item.itemCd, current + item.checkQty);
    });
    return map;
  }, [checkedList]);

  // 노란색 행: 검수된 품목만 (스캔만 한 품목은 제외)
  const checkedItemCdSet = useMemo(() => {
    const set = new Set<string>();
    checkedList.forEach((item) => set.add(item.itemCd));
    return set;
  }, [checkedList]);

  const rowClassRules = useMemo(
    () => ({
      'pda1040-active-row': (params: RowClassParams<PDA1040_R01DetailDtoWithInserted>) => {
        const itemCd = params.data?.itemCd;
        return !!itemCd && checkedItemCdSet.has(itemCd);
      },
    }),
    [checkedItemCdSet]
  );

  const paddedGridData = useMemo((): PDA1040_R01DetailDtoWithInserted[] => {
    // 상단 우선순위: 현재 스캔 품목 → 최근 검수 품목(역순) → 나머지(기존 itemSeq 순)
    const priorityOrder: string[] = [];

    if (scannedItemCd) {
      priorityOrder.push(scannedItemCd);
    }

    [...checkedList].reverse().forEach((item) => {
      if (!priorityOrder.includes(item.itemCd)) {
        priorityOrder.push(item.itemCd);
      }
    });

    if (priorityOrder.length === 0) {
      return [...details];
    }

    const prioritySet = new Set(priorityOrder);
    const topRows: PDA1040_R01DetailDtoWithInserted[] = [];
    const restRows: PDA1040_R01DetailDtoWithInserted[] = [];

    details.forEach((detail) => {
      if (prioritySet.has(detail.itemCd)) {
        topRows.push(detail);
      } else {
        restRows.push(detail);
      }
    });

    topRows.sort(
      (a, b) => priorityOrder.indexOf(a.itemCd) - priorityOrder.indexOf(b.itemCd)
    );

    return [...topRows, ...restRows];
  }, [details, checkedList, scannedItemCd]);

  const columnDefs = useMemo(
    () => [
      {
        headerName: '품명',
        field: 'itemNm',
        valueGetter: (params) => {
          const itemNm = params.data?.itemNm || '-';
          return itemNm;
        },
        cellStyle: (params) => {
          const itemCd = params.data?.itemCd;
          const isActive = !!itemCd && checkedItemCdSet.has(itemCd);
          return {
            textAlign: 'left',
            ...pdaCellStyle,
            whiteSpace: 'pre-line',
            ...(isActive ? { backgroundColor: ACTIVE_ROW_BG } : {}),
          };
        },
        headerClass: aggridHeaderClass,
        flex: 2,
        minWidth: 90,
      },
      {
        headerName: '발주량',
        field: 'orderQty',
        valueGetter: (params) => params.data?.orderQty ?? null,
        valueFormatter: (params: ValueFormatterParams) => {
          if (params.value === null || params.value === undefined) return '-';
          return params.value.toLocaleString();
        },
        cellStyle: (params) => {
          const itemCd = params.data?.itemCd;
          const isActive = !!itemCd && checkedItemCdSet.has(itemCd);
          return {
            textAlign: 'right',
            ...pdaCellStyle,
            justifyContent: 'flex-end',
            ...(isActive ? { backgroundColor: ACTIVE_ROW_BG } : {}),
          };
        },
        headerClass: aggridHeaderClass,
        flex: 1,
        minWidth: 80,
      },
      {
        headerName: '입고량',
        field: 'transportQty',
        valueGetter: (params) => params.data?.transportQty ?? 0,
        valueFormatter: (params: ValueFormatterParams) => {
          const value = params.value ?? 0;
          return value.toLocaleString();
        },
        cellStyle: (params) => {
          const detail = params.data as PDA1040_R01DetailDto;
          if (!detail) {
            return {
              textAlign: 'right',
              ...pdaCellStyle,
              justifyContent: 'flex-end',
            };
          }

          const isActive = checkedItemCdSet.has(detail.itemCd);
          let backgroundColor = 'white';
          if (isActive) {
            backgroundColor = ACTIVE_ROW_BG;
          } else if (detail.remainQty === 0) {
            backgroundColor = '#dcfce7'; // green-100
          } else if (detail.transportQty > 0) {
            backgroundColor = ACTIVE_ROW_BG;
          }

          return {
            textAlign: 'right',
            ...pdaCellStyle,
            backgroundColor,
            fontWeight: 'bold',
            color: '#2563eb',
            justifyContent: 'flex-end',
          };
        },
        headerClass: aggridHeaderClass,
        flex: 1,
        minWidth: 80,
      },
      {
        headerName: '검수량',
        field: 'checkQty',
        valueGetter: (params) => {
          const detail = params.data as PDA1040_R01DetailDto;
          if (!detail) return 0;
          return checkedQtyMap.get(detail.itemCd) || 0;
        },
        valueFormatter: (params: ValueFormatterParams) => {
          const value = params.value ?? 0;
          return value.toLocaleString();
        },
        cellStyle: (params) => {
          const detail = params.data as PDA1040_R01DetailDto;
          const value = params.value ?? 0;
          const isActive = !!detail && checkedItemCdSet.has(detail.itemCd);
          return {
            textAlign: 'right',
            ...pdaCellStyle,
            color: value > 0 ? '#7c3aed' : '#9ca3af',
            fontWeight: value > 0 ? 'bold' : 'normal',
            justifyContent: 'flex-end',
            ...(isActive ? { backgroundColor: ACTIVE_ROW_BG } : {}),
          };
        },
        headerClass: aggridHeaderClass,
        flex: 1,
        minWidth: 80,
      },
    ] as ColDef<PDA1040_R01DetailDtoWithInserted>[],
    [checkedQtyMap, checkedItemCdSet]
  );

  const initSelected = () => {};

  return (
    <div className="pda1040-r01-table flex-1 p-3 pt-2 flex w-full h-full flex-col overflow-hidden">
      <style>{`
        .pda1040-r01-table .ag-row.pda1040-active-row .ag-cell {
          background-color: ${ACTIVE_ROW_BG};
        }
      `}</style>
      <GridContextWrapper<PDA1040_R01DetailDtoWithInserted>
        gridRef={gridRef}
        gridWrapperRef={gridWrapperRef}
        paddedGridData={paddedGridData}
        initSelected={initSelected}
        baseColumnDefs={columnDefs}
        setColumnState={setColumnState}
        localStorageKey="PDA1040_R01_Table_ColumnState"
        enableDataHistory={false}
      >
        <div className="flex-1 w-full h-full min-h-0">
          <Grid_Select<PDA1040_R01DetailDtoWithInserted>
            ref={gridRef}
            rowData={paddedGridData}
            columnDefs={columnDefs}
            rowClassRules={rowClassRules}
            setHoveredRowIndex={setHoveredRowIndex}
            columnStateKey="PDA1040_R01_Table_ColumnState"
            setColumnState={setColumnState}
          />
        </div>
      </GridContextWrapper>
    </div>
  );
};

export default PDA1040_R01Table;
